<template>
  <login-dialog-fi
    v-if="showLoginDialog"
    ref="loginDialog"
    :init-login
    :is-login-failure="isLoginFailure"
    :authorize-redirect-query="authorizeRedirectQuery"
  />
</template>

<script setup lang="ts">
import { useLoginStore } from 'store/login';
import { useProfileFlyoutStore } from 'store/profile-flyout';
import { defineAsyncComponent, onMounted, ref, useTemplateRef } from 'vue';

import { useDict, useFischer } from '@/composables/fischerPlugin';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import { useNotificationStore } from '@/store/pinia/notification';

defineOptions({
  name: 'LoginDialogHandlerFi',
});

const LoginDialogFi = defineAsyncComponent(
  () => import('@/components/login-dialog-fi/LoginDialogFi.vue'),
);

const fischer = useFischer();
const dict = useDict();
const loginStore = useLoginStore();
const notificationStore = useNotificationStore();
const profileFlyoutStore = useProfileFlyoutStore();

const loginDialog = useTemplateRef<typeof LoginDialogFi>('loginDialog');

const showLoginDialog = ref<boolean>(false);
const initLogin = ref<boolean>(true);
const isLoginFailure = ref<boolean>(false);

const login = () => {
  if (!fischer.isLoginDialogEnabled()) {
    console.error('login dialog is not enabled');
    return;
  }
  showLoginDialog.value = true;
  loginDialog.value?.open(); // when dialog was opened before
};

const authorizeRedirectQuery = ref<string>('');

const clearHash = () => {
  if (!window.location.hash) {
    return;
  }
  let cleanedHash = String(window.location.hash)
    .replace(/(^#|&)logged-in=true(?:$|&)/, (_, separator) => separator)
    .replace(/&$/, '');
  const urlNoParams = `${fischer.uri()}${window.location.search}${cleanedHash}`;
  try {
    window.history.replaceState(null, '', urlNoParams);
  } catch (error) {
    console.error('Could not remove search', error);
  }
};

onMounted(() => {
  if (fischer.user.authenticated()) {
    if (/(?:^#|&)logged-in=true(?:$|&)/.test(window.location.hash)) {
      clearHash();
      if (fischer.isProfileFlyoutEnabled()) {
        if (!profileFlyoutStore.consumePreventFlyout()) {
          profileFlyoutStore.showProfileFlyout = true;
        }
      } else {
        let value = `<div>${dict.get('online-Shop.Global-Buttons.Login-Success')}</div>`;

        const startPage =
          fischer.settings()?.shop?.clientIdOverwrite !== 'fiwe-zz-fixperience' &&
          fischer.baseSiteId() !== 'FITE-INT' &&
          fischer.user.myfischerStartPageUrl();
        if (startPage) {
          value = `${value}<div class="fi-text-left"><a style="margin-bottom: 0px" href="${startPage}">${dict.get('online-Shop.Global-Buttons.Login-Start-Page')}</a></div>`;
        }
        notificationStore.openToastNotification({
          message: {
            value,
          },
          type: 'success',
          icon: 'info',
          delayTime: 10000,
          close: true,
        });
      }
    }

    loginStore.loggedIn = true;
    loginStore.urlParsed = true;
    return;
  }

  const brand = fischer.query('brand');
  const urlNoParams = fischer.uri();

  if (urlNoParams === fischer.pages.login().url || brand?.includes('@aslogin')) {
    loginStore.urlParsed = true;
    return;
  }

  if (brand?.startsWith('dialog:')) {
    isLoginFailure.value = fischer.query('authentication_error') === 'true';
    authorizeRedirectQuery.value = fischer.query('authorize_redirect') ?? '';
    clearHash();

    initLogin.value = false;
    loginStore.urlParsed = 'IS_LOGIN';
    login();
  } else {
    type FischerPage = keyof FischerPlugin['pages'];
    const dialogPages: FischerPage[] = ['login', 'registration', 'passwordForgotten'];
    const dialogHash = dialogPages.find((dialog) =>
      new RegExp(`(?:^#|&)${dialog}-dialog(?:$|&)`).test(window.location.hash),
    );
    if (dialogHash) {
      clearHash();

      loginStore.urlParsed = 'IS_LOGIN';
      if (fischer.app.isAppView()) {
        const url = fischer.pages[dialogHash]().url;
        if (url) {
          window.location.href = url;
        } else {
          console.error('missing url to redirect', dialogHash);
        }
        return;
      } else {
        loginStore.triggerLogin({ modal: dialogHash });
        login();
      }
    } else {
      loginStore.urlParsed = true;
    }
  }
});

defineExpose({
  login,
});
</script>
